import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  HStack,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { Page } from '../../types';
import ProgramInfoHeader from './ProgramInfoHeader';
import ProgramDetail from './ProgramDetail';
import { Link, useParams } from 'react-router-dom';
import { ArrowBackIcon, ArrowRightIcon } from '@chakra-ui/icons';

import {
  useGetAllProgramInfoQuery,
  useGetProgramInfoLazyQuery,
  useGetProgramInfoQuery,
} from '../../generated/graphqlModels';
import DemoButtonGroup from './DemoButtonGroup';
import LeadGenerationModal from './LeadGenerationModal';

const BackButton = () => {
  return (
    <Link to="/experience">
      <Button
        leftIcon={<ArrowBackIcon />}
        colorScheme="blue"
        variant="ghost"
        my={4}
      >
        All programs
      </Button>
    </Link>
  );
};

const ProgramInfoPage: Page = () => {
  const { id, slug } = useParams() as any;
  const { data: allPrograms } = useGetAllProgramInfoQuery({ skip: !slug });
  const [modalOpen, setModalOpen] = useState(false);
  const [getProgramInfo, { data, loading, error }] =
    useGetProgramInfoLazyQuery();
  useEffect(() => {
    if (!slug) {
      getProgramInfo({
        variables: {
          programInfoPk: id,
        },
      });
    } else {
      const program = allPrograms?.programInfos.find((p) => p.slug === slug);
      if (program) {
        getProgramInfo({
          variables: {
            programInfoPk: program.id,
          },
        });
      }
    }
  }, [allPrograms, slug]);

  const donwloadScopeAndSequence = () => {
    if (data && data.programInfo && data.programInfo.scopeAndSequence?.url) {
      let a = document.createElement('a');
      a.href = data.programInfo.scopeAndSequence?.url;
      a.target = '_blank';
      a.rel = 'noreferrer';
      a.download = data.programInfo.scopeAndSequence?.name;
      a.click();
      a.remove();
    }
  };

  if (loading) {
    return (
      <Container maxW={'8xl'}>
        <BackButton />
        <HStack mt={8}>
          <Skeleton height="320px" width="600px" />
          <SkeletonText
            ml="4"
            noOfLines={3}
            spacing="4"
            skeletonHeight="4"
            w="100%"
          />
        </HStack>
        <SkeletonText mt="4" noOfLines={8} spacing="4" skeletonHeight="2" />
      </Container>
    );
  }
  if (!data?.programInfo) {
    return (
      <Container maxW={'7xl'}>
        <BackButton />
      </Container>
    );
  }
  return (
    <Container maxW={'8xl'} paddingBottom={[16, 16, 24]}>
      <BackButton />
      <VStack gap={[16, 16, 24]} spacing={0}>
        <ProgramInfoHeader
          {...(data.programInfo as any)}
          setModalOpen={setModalOpen}
          donwloadScopeAndSequence={donwloadScopeAndSequence}
        />
        <ProgramDetail {...(data.programInfo as any)} />
        <DemoButtonGroup
          demo={data?.programInfo.demo || undefined}
          scopeAndSequenceUrl={data.programInfo.scopeAndSequence?.url}
          setModalOpen={setModalOpen}
          download={donwloadScopeAndSequence}
          programName={data.programInfo.name || 'Program Name'}
        />
      </VStack>
      <LeadGenerationModal
        program={data.programInfo.name}
        isOpen={modalOpen}
        download={donwloadScopeAndSequence}
        close={() => {
          setModalOpen(false);
        }}
      ></LeadGenerationModal>
    </Container>
  );
};

ProgramInfoPage.getRoute = () =>
  ['/info/:id', '/explore/:slug', '/experience/:slug'] as any;

export default ProgramInfoPage;
